// src/lib/bag.ts
export type Club = { name: string; carryYards: number };

export const DEFAULT_BAG: Club[] = [
  { name: "Lob wedge", carryYards: 60 },
  { name: "Sand wedge", carryYards: 75 },
  { name: "Gap wedge", carryYards: 90 },
  { name: "Pitching wedge", carryYards: 105 },
  { name: "9-iron", carryYards: 116 },
  { name: "8-iron", carryYards: 128 },
  { name: "7-iron", carryYards: 138 },
  { name: "6-iron", carryYards: 149 },
  { name: "5-iron", carryYards: 157 },
  { name: "4-iron / hybrid", carryYards: 170 },
  { name: "3-hybrid / 5-wood", carryYards: 183 },
  { name: "3-wood", carryYards: 195 },
];

// -----------------------
// Type guards (schema lock)
// -----------------------
export function isClub(x: unknown): x is Club {
  if (!x || typeof x !== "object") return false;
  const o = x as any;
  return typeof o.name === "string" && typeof o.carryYards === "number" && Number.isFinite(o.carryYards);
}

export function isClubArray(x: unknown): x is Club[] {
  return Array.isArray(x) && x.every(isClub);
}

// -----------------------
// Canonical bag function
// -----------------------
export function normalizeBag(bag: unknown): Club[] {
  // 1) Normalize arbitrary/legacy inputs into {name, carryYards}
  if (!Array.isArray(bag)) return DEFAULT_BAG;

  const incoming: Club[] = bag
    .filter(Boolean)
    .map((c: any) => ({
      name: String(c?.name ?? "").trim(),
      // accept legacy keys: carryYards | carryYds | carry
      carryYards: Number(c?.carryYards ?? c?.carryYds ?? c?.carry ?? 0),
    }))
    .filter((c) => c.name.length > 0)
    .map((c) => ({
      name: c.name,
      carryYards: Number.isFinite(c.carryYards) ? c.carryYards : 0,
    }));

  // 2) Merge into DEFAULT_BAG by name so UI is never “blank”
  const byName = new Map(incoming.map((c) => [c.name, c.carryYards]));
  const merged = DEFAULT_BAG.map((d) => ({
    name: d.name,
    carryYards: Number(byName.get(d.name)) || d.carryYards,
  }));

  return merged;
}

// -----------------------
// Console assertion helper (never silent)
// -----------------------
export function assertBagShape(where: string, bag: unknown) {
  const ok =
    Array.isArray(bag) &&
    bag.length > 0 &&
    (bag as any[]).every((c) => c && typeof c.name === "string" && Number.isFinite(Number(c.carryYards ?? c.carryYds ?? c.carry)));

  console.assert(ok, `[SS][ASSERT] bag shape invalid at ${where}`, bag);
}